import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { 
  Home, FileText, Briefcase, Users, CreditCard, UserCheck,
  ChevronLeft, ChevronRight, Bell, Settings, LogOut,
  Building2, List, Receipt, TrendingUp, RefreshCw, BarChart3
} from 'lucide-react';
import './App.css';

// Pages
import Dashboard from './pages/Dashboard';
import DevisList from './pages/DevisList';
import DevisDetail from './pages/DevisDetail';
import AffairesList from './pages/AffairesList';
import AffaireDetail from './pages/AffaireDetail';
import ClientsList from './pages/ClientsList';
import ClientDetail from './pages/ClientDetail';
import Facturation from './pages/Facturation';
import FactureForm from './pages/FactureForm';
import SousTraitants from './pages/SousTraitants';
import Statistiques from './pages/Statistiques';

import { societes, agences } from './data/mockData';

// ==========================================
// Contexte global société/agence
// ==========================================
export const FiltersContext = createContext();

export const useFilters = () => useContext(FiltersContext);

// ==========================================
// Barre de sélection Société / Agence
// ==========================================
const SocieteAgenceBar = ({ selectedSociete, setSelectedSociete, selectedAgence, setSelectedAgence }) => {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      padding: '0.625rem 1.5rem',
      background: 'white', borderBottom: '1px solid var(--gray-200)',
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      <Building2 size={16} style={{ color: 'var(--gray-400)' }} />
      <span style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', fontWeight: 500 }}>Périmètre :</span>

      <div style={{ display: 'flex', gap: '0.375rem' }}>
        <button
          onClick={() => setSelectedSociete(null)}
          style={{
            padding: '0.375rem 0.75rem', borderRadius: '9999px', border: 'none',
            fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-sans)',
            background: !selectedSociete ? 'var(--primary)' : 'var(--gray-100)',
            color: !selectedSociete ? 'white' : 'var(--gray-600)',
            transition: 'all 0.15s',
          }}
        >
          Toutes sociétés
        </button>
        {societes.map(s => (
          <button
            key={s.id}
            onClick={() => setSelectedSociete(selectedSociete === s.id ? null : s.id)}
            style={{
              padding: '0.375rem 0.75rem', borderRadius: '9999px', border: 'none',
              fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-sans)',
              background: selectedSociete === s.id ? 'var(--primary)' : 'var(--gray-100)',
              color: selectedSociete === s.id ? 'white' : 'var(--gray-600)',
              transition: 'all 0.15s',
            }}
          >
            {s.nom}
          </button>
        ))}
      </div>

      <div style={{ width: '1px', height: '24px', background: 'var(--gray-200)', margin: '0 0.25rem' }} />

      <div style={{ display: 'flex', gap: '0.375rem' }}>
        <button
          onClick={() => setSelectedAgence(null)}
          style={{
            padding: '0.375rem 0.75rem', borderRadius: '9999px', border: 'none',
            fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-sans)',
            background: !selectedAgence ? 'var(--accent)' : 'var(--gray-100)',
            color: !selectedAgence ? 'white' : 'var(--gray-600)',
            transition: 'all 0.15s',
          }}
        >
          Toutes agences
        </button>
        {agences.map(a => (
          <button
            key={a.id}
            onClick={() => setSelectedAgence(selectedAgence === a.id ? null : a.id)}
            style={{
              padding: '0.375rem 0.75rem', borderRadius: '9999px', border: 'none',
              fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-sans)',
              background: selectedAgence === a.id ? 'var(--accent)' : 'var(--gray-100)',
              color: selectedAgence === a.id ? 'white' : 'var(--gray-600)',
              transition: 'all 0.15s',
            }}
          >
            {a.nom}
          </button>
        ))}
      </div>

      {(selectedSociete || selectedAgence) && (
        <button
          onClick={() => { setSelectedSociete(null); setSelectedAgence(null); }}
          style={{
            marginLeft: 'auto', padding: '0.375rem 0.625rem', borderRadius: 'var(--radius-sm)',
            border: 'none', background: 'var(--gray-100)', color: 'var(--gray-500)',
            fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'var(--font-sans)',
            display: 'flex', alignItems: 'center', gap: '0.25rem'
          }}
        >
          <RefreshCw size={12} /> Réinitialiser
        </button>
      )}
    </div>
  );
};

// ==========================================
// Navigation
// ==========================================
const Navigation = ({ isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  
  const navItems = [
    { section: 'Principal' },
    { path: '/', icon: Home, label: 'Tableau de bord' },
    
    { section: 'Devis (TIIME)' },
    { path: '/devis', icon: FileText, label: 'Liste des devis' },
    
    { section: 'Affaires' },
    { path: '/affaires', icon: Briefcase, label: 'Liste des affaires' },
    
    { section: 'Clients & Partenaires' },
    { path: '/clients', icon: Users, label: 'Clients' },
    { path: '/sous-traitants', icon: UserCheck, label: 'Sous-traitants' },
    
    { section: 'Facturation' },
    { path: '/facturation', icon: TrendingUp, label: 'Tableau prévisionnel' },
    { path: '/factures/nouvelle', icon: Receipt, label: 'Créer une facture' },
    
    { section: 'KPIs' },
    { path: '/statistiques', icon: BarChart3, label: 'Statistiques' },
  ];

  return (
    <nav className={`navigation ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="nav-header">
        <div className="nav-brand">
          <Building2 className="brand-icon" />
          {!isCollapsed && <h1>JOVAL</h1>}
        </div>
        <button className="nav-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {!isCollapsed && (
        <div className="nav-user">
          <div className="user-avatar" style={{ background: 'linear-gradient(135deg, #2563EB, #1E40AF)' }}>
            AG
          </div>
          <div className="user-info">
            <p className="user-name">Assistante Gestion</p>
            <p className="user-role">Multi-société</p>
          </div>
        </div>
      )}

      <ul className="nav-menu">
        {navItems.map((item, index) => {
          if (item.section) {
            return !isCollapsed && (
              <li key={index} className="nav-section">{item.section}</li>
            );
          }
          const Icon = item.icon;
          return (
            <li key={index}>
              <NavLink
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive && location.pathname === item.path ? 'active' : ''}`}
                title={isCollapsed ? item.label : ''}
              >
                <Icon size={20} />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            </li>
          );
        })}
      </ul>

      <div className="nav-footer">
        {!isCollapsed && (
          <>
            <div className="nav-footer-actions">
              <button className="btn-icon" title="Notifications"><Bell size={18} /></button>
              <button className="btn-icon" title="Paramètres"><Settings size={18} /></button>
              <button className="btn-icon" title="Déconnexion"><LogOut size={18} /></button>
            </div>
            <div className="nav-footer-info">
              <p>Sociétés</p>
              <strong>JOVAL & JOVAL CONSEIL</strong>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

// ==========================================
// Main App
// ==========================================
function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedSociete, setSelectedSociete] = useState(null);
  const [selectedAgence, setSelectedAgence] = useState(null);

  const filters = {
    societeId: selectedSociete,
    agenceId: selectedAgence,
  };

  return (
    <FiltersContext.Provider value={filters}>
      <Router>
        <div className="app">
          <Navigation isCollapsed={sidebarCollapsed} setIsCollapsed={setSidebarCollapsed} />
          <main className={`main-content ${sidebarCollapsed ? 'sidebar-closed' : ''}`}>
            <SocieteAgenceBar
              selectedSociete={selectedSociete}
              setSelectedSociete={setSelectedSociete}
              selectedAgence={selectedAgence}
              setSelectedAgence={setSelectedAgence}
            />
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/devis" element={<DevisList />} />
              <Route path="/devis/:id" element={<DevisDetail />} />
              <Route path="/affaires" element={<AffairesList />} />
              <Route path="/affaires/:id" element={<AffaireDetail />} />
              <Route path="/clients" element={<ClientsList />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/facturation" element={<Facturation />} />
              <Route path="/factures/nouvelle" element={<FactureForm />} />
              <Route path="/sous-traitants" element={<SousTraitants />} />
              <Route path="/statistiques" element={<Statistiques />} />
            </Routes>
          </main>
        </div>
      </Router>
    </FiltersContext.Provider>
  );
}

export default App;
